import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { EventSummary } from '@/lib/types';

import { SceneFormValues } from '../schemas';

export function useSceneEventAutoFill(
  form: UseFormReturn<SceneFormValues>,
  events: EventSummary[] | null | undefined,
  isLoadingEvents: boolean
) {
  const { watch, setValue, getValues } = form;
  const selectedEventIds = watch('eventIds') || [];
  const prevEventIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!events || isLoadingEvents) return;

    const addedIds = selectedEventIds.filter(id => !prevEventIdsRef.current.includes(id));

    if (addedIds.length > 0) {
      const currentCharaIds = getValues('characterIds') || [];
      const currentOrgIds = getValues('organizationIds') || [];
      const currentLocIds = getValues('locationIds') || [];

      const newCharacterIds = new Set(currentCharaIds);
      const newOrgIds = new Set(currentOrgIds);
      const newLocIds = new Set(currentLocIds);

      let changed = false;

      addedIds.forEach(eventId => {
        const event = events.find(e => e.id === eventId);
        if (event) {
          if (event.characterIds) {
            event.characterIds.forEach((id: string) => {
              if (!newCharacterIds.has(id)) {
                newCharacterIds.add(id);
                changed = true;
              }
            });
          }
          if (event.organizationIds) {
            event.organizationIds.forEach((id: string) => {
              if (!newOrgIds.has(id)) {
                newOrgIds.add(id);
                changed = true;
              }
            });
          }
          if (event.locationIds) {
            event.locationIds.forEach((id: string) => {
              if (!newLocIds.has(id)) {
                newLocIds.add(id);
                changed = true;
              }
            });
          }
        }
      });

      if (changed) {
        setValue('characterIds', Array.from(newCharacterIds));
        setValue('organizationIds', Array.from(newOrgIds));
        setValue('locationIds', Array.from(newLocIds));
      }
    }
    prevEventIdsRef.current = selectedEventIds;
  }, [events, isLoadingEvents, selectedEventIds, getValues, setValue]);
}
